import React, { useEffect, useRef, useState } from "react";
import { ExactFit, resizeImageToJpeg, saveBlob } from "src/utils/downloadFileAs";

// "Resize" for the PAN form's photograph and signature, laid out like an image
// editor's Resize dialog: pixels across, pixels down, "Maintain aspect ratio",
// plus the file-size cap and DPI the PAN portals ask for. The result is
// previewed live (pixels + KB) and downloaded as `<ack>_Photo.jpg` /
// `<ack>_Sig.jpg` for the operator to upload to the portal.
//
// It does NOT change the image in the form. The signature there is a
// transparent PNG because it also prints as the cross sign over the photo; a
// JPG on white would print a white box across the photograph instead.

export type ResizePreset = "photo" | "sign";

/**
 * The PAN portal sizes, both exact: photo 204 x 204 cropped to fill, signature
 * 333 x 137 with the whole signature stretched to fill it.
 */
const PRESETS: Record<
  ResizePreset,
  { label: string; width: number; height: number; maxKB: number; fit: ExactFit }
> = {
  photo: { label: "PAN photo 204 x 204, 9 KB", width: 204, height: 204, maxKB: 9, fit: "fill" },
  sign: { label: "PAN sign 333 x 137, 7 KB", width: 333, height: 137, maxKB: 7, fit: "fill" },
};

type Props = {
  /** The image as it is in the form (data-URL or remote URL). */
  src: string;
  preset: ResizePreset;
  /** Download name without the extension, e.g. `910364200209262_Photo`. */
  fileName: string;
  onClose: () => void;
};

type Result = { url: string; blob: Blob; width: number; height: number };

const ImageResizePanel = ({ src, preset, fileName, onClose }: Props) => {
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [keepRatio, setKeepRatio] = useState(true);
  const [maxKB, setMaxKB] = useState(PRESETS[preset].maxKB);
  const [dpi, setDpi] = useState(200);
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  /** Fill the boxes from the preset, against the image's own shape. */
  const applyPreset = (nat: { w: number; h: number }) => {
    const p = PRESETS[preset];
    // Exact size: the shape is the preset's, not the image's; the image is
    // stretched to fill it, nothing cut, no white (photo and sign) — p.fit, resizeImageToJpeg.
    setWidth(p.width);
    setHeight(p.height);
    setKeepRatio(false);
    setMaxKB(p.maxKB);
    setDpi(200);
  };

  // Natural size of the image, for the aspect ratio and the preset.
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const nat = { w: img.naturalWidth, h: img.naturalHeight };
      setNatural(nat);
      applyPreset(nat);
    };
    img.onerror = () => setError("Could not load the image.");
    img.src = src;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const onWidth = (w: number) => {
    setWidth(w);
    if (keepRatio && natural) setHeight(Math.max(1, Math.round((w * natural.h) / natural.w)));
  };
  const onHeight = (h: number) => {
    setHeight(h);
    if (keepRatio && natural) setWidth(Math.max(1, Math.round((h * natural.w) / natural.h)));
  };
  const onKeepRatio = (keep: boolean) => {
    setKeepRatio(keep);
    // Re-ticking snaps the height back to the image's shape, as an editor does.
    if (keep && natural && width) setHeight(Math.max(1, Math.round((width * natural.h) / natural.w)));
  };

  // Live result, debounced so typing a number does not encode on every key.
  const lastUrl = useRef<string>("");
  useEffect(() => {
    if (!natural || width < 1 || height < 1) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setBusy(true);
      try {
        const blob = await resizeImageToJpeg(src, {
          width,
          height,
          maxBytes: maxKB > 0 ? Math.round(maxKB * 1024) : undefined,
          dpi: dpi > 0 ? dpi : undefined,
          fit: PRESETS[preset].fit,
        });
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        if (lastUrl.current) URL.revokeObjectURL(lastUrl.current);
        lastUrl.current = url;
        setResult({ url, blob, width, height });
        setError("");
      } catch (e: any) {
        if (cancelled) return;
        setResult(null);
        setError(e?.message || "Could not resize the image.");
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [src, natural, width, height, maxKB, dpi, preset]);

  useEffect(
    () => () => {
      if (lastUrl.current) URL.revokeObjectURL(lastUrl.current);
    },
    []
  );

  const stretched =
    !!natural &&
    !keepRatio &&
    width > 0 &&
    height > 0 &&
    Math.abs(width / height - natural.w / natural.h) > 0.02;

  const box: React.CSSProperties = {
    marginTop: "8px",
    border: "1px solid #ddd",
    padding: "8px 10px",
    fontSize: "12px",
    background: "#fafafa",
  };
  const row: React.CSSProperties = { display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" };
  const num: React.CSSProperties = { width: "70px", fontSize: "12px", padding: "2px 4px" };
  const btn: React.CSSProperties = { fontSize: "12px", padding: "4px 10px", cursor: "pointer" };

  return (
    <div style={box}>
      <div style={{ ...row, justifyContent: "space-between", fontWeight: 600 }}>
        <span>Resize (pixels)</span>
        {natural && (
          <span style={{ fontWeight: 400, color: "#666" }}>
            Now: {natural.w} x {natural.h} px
          </span>
        )}
      </div>
      <div style={row}>
        <span style={{ width: "70px" }}>Horizontal</span>
        <input
          type="number"
          min={1}
          max={5000}
          value={width || ""}
          onChange={(e) => onWidth(Number(e.target.value))}
          style={num}
        />
        <span>px</span>
      </div>
      <div style={row}>
        <span style={{ width: "70px" }}>Vertical</span>
        <input
          type="number"
          min={1}
          max={5000}
          value={height || ""}
          onChange={(e) => onHeight(Number(e.target.value))}
          style={num}
        />
        <span>px</span>
      </div>
      <label style={{ ...row, cursor: "pointer" }}>
        <input type="checkbox" checked={keepRatio} onChange={(e) => onKeepRatio(e.target.checked)} />
        Maintain aspect ratio
      </label>
      <div style={row}>
        <span style={{ width: "70px" }}>Max size</span>
        <input
          type="number"
          min={0}
          step={0.5}
          value={maxKB}
          onChange={(e) => setMaxKB(Number(e.target.value))}
          style={num}
          title="0 = no limit"
        />
        <span>KB</span>
        <span style={{ width: "30px" }} />
        <span>DPI</span>
        <input
          type="number"
          min={0}
          max={1200}
          value={dpi}
          onChange={(e) => setDpi(Number(e.target.value))}
          style={{ ...num, width: "56px" }}
        />
      </div>
      {stretched && (
        <div style={{ color: "#a15c00", marginBottom: "6px" }}>
          The image is not this shape, so it will be stretched. Crop it to shape
          first (Crop / Adjust) to avoid that.
        </div>
      )}

      <div style={{ ...row, alignItems: "flex-start" }}>
        {result && (
          <img
            src={result.url}
            alt="Resized"
            style={{ maxWidth: "120px", maxHeight: "90px", border: "1px solid #ccc", background: "#fff" }}
          />
        )}
        <div>
          {busy && <div style={{ color: "#666" }}>Working…</div>}
          {!busy && result && (
            <div>
              <b>
                {result.width} x {result.height} px
              </b>
              {" · "}
              {(result.blob.size / 1024).toFixed(1)} KB
              {dpi > 0 ? ` · ${dpi} DPI` : ""} · JPG
            </div>
          )}
          {!busy && error && <div style={{ color: "#c62828" }}>{error}</div>}
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        <button
          type="button"
          style={btn}
          disabled={!result || busy}
          onClick={() => result && saveBlob(result.blob, `${fileName}.jpg`)}
        >
          Download JPG
        </button>
        <button
          type="button"
          style={btn}
          disabled={!natural}
          onClick={() => natural && applyPreset(natural)}
          title="Reset to the PAN portal size"
        >
          {PRESETS[preset].label}
        </button>
        <button type="button" style={btn} onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

export default ImageResizePanel;
